import { t } from "../i18n";

//The mailboxes our customers actually use. A domain close to one of these is
//taken for a typo of it; an exact match is left alone, so ymail.com is never
//"corrected" to gmail.com. yahoo.com comes before yahoo.co.in: "yahoo.co" is
//a prefix of both and the first one wins.
const KNOWN_DOMAINS = [
	"gmail.com",
	"yahoo.com",
	"yahoo.co.in",
	"yahoo.in",
	"yahoo.co.uk",
	"hotmail.com",
	"outlook.com",
	"rediffmail.com",
	"icloud.com",
	"live.com",
	"ymail.com",
	"myyahoo.com",
	"googlemail.com",
];

//Real mailboxes that sit close enough to one of the above to read as its
//typo: mail.com is one edit from gmail.com, yahoo.com.au "extends" yahoo.com.
//Left alone as typed, and never offered as a correction.
const OTHER_REAL_DOMAINS = [
	"mail.com",
	"gmx.com",
	"gmx.de",
	"gmx.net",
	"web.de",
	"aol.com",
	"msn.com",
	"me.com",
	"mac.com",
	"protonmail.com",
	"proton.me",
	"pm.me",
	"zoho.com",
	"zohomail.in",
	"yandex.com",
	"yandex.ru",
	"mail.ru",
	"rocketmail.com",
	"windowslive.com",
	"yahoo.fr",
	"yahoo.de",
	"yahoo.it",
	"yahoo.es",
	"yahoo.ca",
	"yahoo.gr",
	"yahoo.ie",
	"yahoo.com.au",
	"yahoo.co.jp",
	"yahoo.co.nz",
	"yahoo.co.za",
	"yahoo.co.id",
	"yahoo.com.br",
	"yahoo.com.mx",
	"yahoo.com.ar",
	"yahoo.com.sg",
	"yahoo.com.my",
	"yahoo.com.ph",
	"yahoo.com.hk",
	"yahoo.com.tw",
	"hotmail.co.uk",
	"hotmail.fr",
	"hotmail.it",
	"hotmail.de",
	"hotmail.es",
	"hotmail.gr",
	"hotmail.ca",
	"hotmail.be",
	"hotmail.nl",
	"hotmail.com.au",
	"outlook.in",
	"outlook.fr",
	"outlook.de",
	"outlook.it",
	"outlook.es",
	"outlook.com.au",
	"live.co.uk",
	"live.fr",
	"live.in",
	"live.it",
	"live.de",
	"live.ca",
	"live.nl",
	"live.com.au",
	"otenet.gr",
	"hol.gr",
	"cytanet.com.cy",
];

const GMAIL_DOMAINS = ["gmail.com", "googlemail.com"];

//Optimal string alignment distance: a swapped pair (gamil) counts as one edit.
function distance(a, b) {
	const d = [];
	for (let i = 0; i <= a.length; i++) {
		d[i] = [i];
	}
	for (let j = 0; j <= b.length; j++) {
		d[0][j] = j;
	}
	for (let i = 1; i <= a.length; i++) {
		for (let j = 1; j <= b.length; j++) {
			const cost = a[i - 1] === b[j - 1] ? 0 : 1;
			d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
			if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
				d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
			}
		}
	}
	return d[a.length][b.length];
}

function suggestDomain(domain) {
	if (KNOWN_DOMAINS.includes(domain) || OTHER_REAL_DOMAINS.includes(domain)) {
		return "";
	}

	const close = closeDomain(domain);
	if (close) {
		return close;
	}

	//432gmail.com, g175001mail.com: digits typed into a known domain
	const plain = domain.replace(/[0-9]/g, "");
	if (plain !== domain && plain.includes(".")) {
		const fixed = KNOWN_DOMAINS.includes(plain) ? plain : closeDomain(plain);
		if (fixed) {
			return fixed;
		}
	}

	//gimal.come: a mistyped name and a mistyped .com together
	const dot = domain.lastIndexOf(".");
	const extension = domain.slice(dot + 1);
	if (dot > 0 && extension !== "com" && distance(extension, "com") === 1) {
		const asCom = domain.slice(0, dot) + ".com";
		return KNOWN_DOMAINS.includes(asCom) ? asCom : closeDomain(asCom);
	}
	return "";
}

function closeDomain(domain) {
	//gmail.comi, yahoo.comnn: the right domain with something typed after it
	const extended = KNOWN_DOMAINS.find((known) => domain.startsWith(known));
	if (extended) {
		return extended;
	}

	//gmail.c, gmail.co: the right domain cut short, at least into its extension
	const cut = KNOWN_DOMAINS.find(
		(known) => known.startsWith(domain) && domain.length > known.indexOf(".") + 1,
	);
	if (cut) {
		return cut;
	}

	//one edit for a short domain, two for a long one: two edits turn most
	//short domains into a known one, real company domains included
	const limit = domain.length > 8 ? 2 : 1;
	let best = "";
	let bestDistance = limit + 1;
	KNOWN_DOMAINS.forEach((known) => {
		const current = distance(domain, known);
		if (current < bestDistance) {
			best = known;
			bestDistance = current;
		}
	});
	return best;
}

//Gmail takes 6 to 30 letters, digits and dots before the @; the dots and a
//+alias do not count.
function breaksGmailRules(local) {
	const name = local.split("+")[0];
	const letters = name.replace(/\./g, "");
	return /[^a-z0-9.]/.test(name) || letters.length < 6 || letters.length > 30;
}

//What is wrong with the address, if anything we can tell from here: a
//suggested correction, or only a warning. Most dead addresses look perfectly
//fine and are left to the bounce handling behind the emails.
export function emailIssue(value) {
	const email = value.trim();
	const at = email.lastIndexOf("@");
	if (at < 1) {
		return null;
	}

	const local = email.slice(0, at);
	const domain = email.slice(at + 1).toLowerCase();

	const suggested = suggestDomain(domain);
	if (suggested) {
		return { suggestion: local + "@" + suggested };
	}
	if (GMAIL_DOMAINS.includes(domain) && breaksGmailRules(local.toLowerCase())) {
		return { suggestion: "" };
	}
	return null;
}

function hintFor(input) {
	const next = input.nextElementSibling;
	return next && next.classList.contains("sdk-email-hint") ? next : null;
}

function clearHint(input) {
	const hint = hintFor(input);
	if (hint) {
		hint.remove();
	}
}

function showHint(input, issue) {
	//Pressing the suggestion blurs the field first: rebuilding the hint then
	//would swap the link out from under the click.
	const current = hintFor(input);
	if (current && current.dataset.email === input.value) {
		return;
	}
	clearHint(input);

	const hint = document.createElement("div");
	hint.className = "sdk-email-hint";
	hint.dataset.email = input.value;

	if (issue.suggestion) {
		const [before, after] = t("emailSuggestion").split("{email}");
		const link = document.createElement("a");
		link.href = "#";
		link.textContent = issue.suggestion;
		link.addEventListener("click", (e) => {
			e.preventDefault();
			input.value = issue.suggestion;
			input.dispatchEvent(new Event("input", { bubbles: true }));
			clearHint(input);
		});
		hint.append(before, link, after);
	} else {
		hint.textContent = t("emailCheck");
	}

	input.insertAdjacentElement("afterend", hint);
}

function emailField(form) {
	return form.querySelector('input[name="email"]');
}

//Called by the submit: true stops this one submit, once per address. The
//customer sees the hint, and a second press sends the address as typed, so a
//rule we got wrong can never cost an order.
export function emailNeedsReview(form) {
	const input = emailField(form);
	if (!input || !input.value.trim()) {
		return false;
	}

	const issue = emailIssue(input.value);
	if (!issue || input.dataset.sdkEmailReviewed === input.value) {
		return false;
	}

	input.dataset.sdkEmailReviewed = input.value;
	showHint(input, issue);
	input.focus();
	return true;
}

export function emailCheck() {
	document.querySelectorAll(".sdk-form").forEach((form) => {
		const input = emailField(form);
		if (!input) {
			return;
		}

		input.addEventListener("blur", () => {
			const issue = input.value.trim() && emailIssue(input.value);
			if (issue) {
				showHint(input, issue);
			} else {
				clearHint(input);
			}
		});
		input.addEventListener("input", () => clearHint(input));
	});
}
